/**
 * pages.config.js - Page routing configuration
 *
 * All pages are lazy-loaded to prevent eagerly initializing heavy dependencies
 * (radix-ui, recharts, etc.) at app startup. Each page's code is only fetched
 * when the user navigates to that route.
 *
 * THE ONLY EDITABLE VALUE: mainPage
 * This controls which page is the landing page (shown when users visit the app).
 *
 * The mainPage value must match a key in the PAGES object exactly.
 */
import { lazy } from 'react';
import __Layout from './Layout.jsx';

// Lazy-loaded pages — only fetched when the route is visited
const Audits = lazy(() => import('./pages/Audits'));
const ChatAssistant = lazy(() => import('./pages/ChatAssistant'));
const Briefs = lazy(() => import('./pages/Briefs'));
// WP02 (DR01): Dashboard route name is kept for deep links; mounts the honest placeholder.
const DashboardPlaceholder = lazy(() => import('./pages/DashboardPlaceholder'));
const Integrations = lazy(() => import('./pages/Integrations'));
// KC v2 is primary at /KnowledgeCenter (Batch 8 swap); legacy page at /knowledge-old
const KnowledgeCenterV2 = lazy(() => import('./pages/KnowledgeCenterV2'));
const KnowledgeCenterLegacy = lazy(() => import('./pages/KnowledgeCenter'));
// WP02 (DR02/DR03): Reports placeholder. Old /ReportBuilder redirects to /Reports (see App.jsx).
const ReportsPlaceholder = lazy(() => import('./pages/ReportsPlaceholder'));
const Settings = lazy(() => import('./pages/Settings'));
const BudgetTargets = lazy(() => import('./pages/BudgetTargets'));
const Team = lazy(() => import('./pages/Team'));
const Profiles = lazy(() => import('./pages/Profiles'));
const SuperAdmin = lazy(() => import('./pages/SuperAdmin'));
const BillingPlans = lazy(() => import('./pages/BillingPlans'));
const BillingSuccess = lazy(() => import('./pages/BillingSuccess'));
const Billing = lazy(() => import('./pages/Billing'));
const AcceptInvitation = lazy(() => import('./pages/AcceptInvitation'));
const Support = lazy(() => import('./pages/Support'));
const LinkSync = lazy(() => import('./pages/LinkSync'));
const AnomalyDetector = lazy(() => import('./pages/AnomalyDetector'));
const Insights        = lazy(() => import('./pages/Insights'));
const Intelligence    = lazy(() => import('./pages/Intelligence'));

// Agency pages (Week 37)
const AgencyCreate = lazy(() => import('./pages/agency/AgencyCreate'));
const AgencyDashboard = lazy(() => import('./pages/agency/AgencyDashboard'));
const AgencyTeam = lazy(() => import('./pages/agency/AgencyTeam'));
const AgencyInvitePM = lazy(() => import('./pages/agency/AgencyInvitePM'));
const AgencyInviteTeam = lazy(() => import('./pages/agency/AgencyInviteTeam'));

// Onboarding pages (Week 36)
const OnboardingConnect = lazy(() => import('./pages/onboarding/OnboardingConnect'));
const OnboardingProfile = lazy(() => import('./pages/onboarding/OnboardingProfile'));
const OnboardingLinkSync = lazy(() => import('./pages/onboarding/OnboardingLinkSync'));
const OnboardingComplete = lazy(() => import('./pages/onboarding/OnboardingComplete'));
const OnboardingAgency = lazy(() => import('./pages/onboarding/OnboardingAgency'));
const OnboardingInvitePM = lazy(() => import('./pages/onboarding/OnboardingInvitePM'));
const OnboardingCreateAccount = lazy(() => import('./pages/onboarding/OnboardingCreateAccount'));
const OnboardingWaitForChannels = lazy(() => import('./pages/onboarding/OnboardingWaitForChannels'));
const OnboardingRoleContext = lazy(() => import('./pages/onboarding/OnboardingRoleContext'));
const OnboardingAboutYou = lazy(() => import('./pages/onboarding/OnboardingAboutYou'));
const OnboardingAdminComplete = lazy(() => import('./pages/onboarding/OnboardingAdminComplete'));
const OnboardingCustomNeeds = lazy(() => import('./pages/onboarding/OnboardingCustomNeeds'));
const OnboardingCustomComplete = lazy(() => import('./pages/onboarding/OnboardingCustomComplete'));

export const PAGES = {
    "Audits": Audits,
    "ChatAssistant": ChatAssistant,
    "Briefs": Briefs,
    "Dashboard": DashboardPlaceholder,
    "Integrations": Integrations,
    "KnowledgeCenter": KnowledgeCenterV2,
    "knowledge-v2": KnowledgeCenterV2,
    "knowledge-old": KnowledgeCenterLegacy,
    "Profiles": Profiles,
    "Reports": ReportsPlaceholder,
    "Settings": Settings,
    "BudgetTargets": BudgetTargets,
    "Team": Team,
    "SuperAdmin": SuperAdmin,
    "Billing": Billing,
    "billing/plans": BillingPlans,
    "billing/success": BillingSuccess,
    "invitation/accept/:token": AcceptInvitation,
    "Support": Support,
    "AnomalyDetector": AnomalyDetector,
    "Insights": Insights,
    "Intelligence": Intelligence,
    "profiles/:id/link-sync": LinkSync,
    // Agency pages (Week 37)
    "agency/create": AgencyCreate,
    "agency/dashboard": AgencyDashboard,
    "agency/team": AgencyTeam,
    "agency/invite-pm": AgencyInvitePM,
    "agency/invite-team": AgencyInviteTeam,
    // Onboarding wizard (Week 36)
    "onboarding/connect": OnboardingConnect,
    "onboarding/connect-account": OnboardingConnect,
    "onboarding/profile": OnboardingProfile,
    "onboarding/link-sync": OnboardingLinkSync,
    "onboarding/complete": OnboardingComplete,
    "onboarding/invite-pm": OnboardingInvitePM,
    "onboarding/create-account": OnboardingCreateAccount,
    "onboarding/wait-for-channels": OnboardingWaitForChannels,
    "onboarding/role-context": OnboardingRoleContext,
    "onboarding/about-you": OnboardingAboutYou,
    "onboarding/create-workspace": OnboardingProfile,
    "onboarding/agency-setup": OnboardingAgency,
    "onboarding/admin-complete": OnboardingAdminComplete,
    "onboarding/custom-needs": OnboardingCustomNeeds,
    "onboarding/custom-complete": OnboardingCustomComplete,
}

export const pagesConfig = {
    mainPage: "Dashboard",
    Pages: PAGES,
    Layout: __Layout,
};
